import React, { FC, ReactNode, useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Element, scroller } from 'react-scroll';

import ChatBotHappyIcon from '~/assets/icons/ChatBotHappyIcon';
import ChatBotIcon from '~/assets/icons/ChatBotIcon';
import ChatBotSadIcon from '~/assets/icons/ChatBotSadIcon';
import AnimatedTyping from '~/components/Test/AnimatedTyping';
import { SCROLLABLE_QUESTION_CONTAINER_ID } from '~/components/Test/const';
import { RadioQuestionProps } from '~/components/Test/types';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { DISCOVER_VIDEOS_ROUTE, ROOT_ROUTE } from '~/routes';
import { SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import { Maybe } from '~/types';
import ProfileAvatar from '~/ui/components/ProfileAvatar';
import { toBoolean } from '~/utils/toBoolean';

import { LeftBubble, RightBubble } from './chatBubbles';

interface ChatQuestionProps extends Omit<RadioQuestionProps, 'colorsMode'> {
  title?: Maybe<string>;
  openExplainer?: () => void;
  continueLearning?: () => void;
}

const DELAY_UNTIL_BOT_RESPOND = 2.5;
const DELAY_UNTIL_ANSWERS_APPEAR = 2;
const DELAY_AFTER_SUBMIT = 1;

const BOTTOM_ELEMENT_NAME = 'chatBottomAnchor';

const bubbleVariants = {
  hidden: { opacity: 0, scale: 0.5, y: 50 },
  visible: { opacity: 1, scale: 1, y: 0 },
};

const visibilityVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

type ResultsDialogAnswer = {
  title: ReactNode;
  onClick: () => void;
};

type ResultsDialog = {
  botResponseTitle: string;
  answers: ResultsDialogAnswer[];
};

const sToMs = (s: number) => s * 1000;

const ChatQuestion: FC<ChatQuestionProps> = ({
  title,
  onSelect,
  isCorrectByIdMap,
  selectedAnswersIds,
  submitted,
  answers,
  isCorrect,
  openExplainer,
  continueLearning,
}) => {
  const { t } = useTenantTranslation();
  const navigate = useNavigate();
  const { userInfo } = useUserInfoContext();
  const [showResponse, setShowResponse] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowResponse(true), sToMs(DELAY_UNTIL_BOT_RESPOND));
  }, []);

  useEffect(() => {
    if (!submitted) {
      return;
    }

    scroller.scrollTo(BOTTOM_ELEMENT_NAME, {
      duration: sToMs(0.9),
      delay: sToMs(DELAY_AFTER_SUBMIT + 0.5),
      smooth: 'easeInOutQuart',
      containerId: SCROLLABLE_QUESTION_CONTAINER_ID,
    });
  }, [submitted]);

  const hasExplainer = !!openExplainer;
  const hasContinueLearning = !!continueLearning;
  const hasCustomActions = hasExplainer || hasContinueLearning;

  const actions = [
    hasExplainer && { title: t('page.video.why'), onClick: openExplainer },
    hasExplainer &&
      hasContinueLearning && { title: t('page.video.continueLearning'), onClick: continueLearning },
    !hasCustomActions && {
      title: t('page.video.discoverMoreVideos'),
      onClick: () => navigate(DISCOVER_VIDEOS_ROUTE),
    },
    !hasCustomActions && {
      title: t('page.video.backToDashboard'),
      onClick: () => navigate(ROOT_ROUTE),
    },
  ].filter(toBoolean<ResultsDialogAnswer>);

  const incorrectResultsDialogue: ResultsDialog = {
    botResponseTitle: hasExplainer
      ? t('page.video.notQuiteExplainWhy')
      : t('page.video.notQuiteKeepLearning'),
    answers: actions,
  };

  const correctResultsDialog: ResultsDialog = {
    botResponseTitle: hasExplainer
      ? t('page.video.wellDoneExplainWhy')
      : t('page.video.wellDoneKeepUp'),
    answers: actions,
  };

  const resultsDialog = isCorrect ? correctResultsDialog : incorrectResultsDialogue;

  return (
    <>
      <LeftMessage>
        <StyledChatBotIcon data-testid="chatBotIcon" />
        <LeftBubble
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          layout={true}
        >
          {showResponse ? (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              data-testid="introMessage"
            >
              {t('common.hello')} {userInfo?.first_name},
              <br />
              <br />
              {t('page.video.hopeYouEnjoyedVideo')}
              <br />
              <br />
              <b>{t('common.question')}:</b> {title}
            </motion.span>
          ) : (
            <AnimatedTyping />
          )}
        </LeftBubble>
      </LeftMessage>
      <RightMessage
        initial={visibilityVariants.hidden}
        animate={visibilityVariants.visible}
        transition={{ delay: DELAY_UNTIL_BOT_RESPOND + DELAY_UNTIL_ANSWERS_APPEAR }}
      >
        <Bubbles>
          {answers.map((answer, index) => {
            const isCorrect = isCorrectByIdMap?.[answer?.id || ''];
            const selected = selectedAnswersIds?.includes(answer?.id || '');

            return (
              <RightBubble
                as={motion.p}
                key={answer?.id}
                selected={selected}
                onClick={() => onSelect(answer?.id)}
                passed={submitted && selected && isCorrect}
                failed={submitted && selected && !isCorrect}
                finished={submitted}
                initial={bubbleVariants.hidden}
                animate={bubbleVariants.visible}
                transition={{
                  delay: 0.3 * index + DELAY_UNTIL_BOT_RESPOND + DELAY_UNTIL_ANSWERS_APPEAR + 0.5,
                }}
                data-testid="quizQuestionAnswer"
              >
                {answer?.title}
              </RightBubble>
            );
          })}
        </Bubbles>
        <ProfileAvatar />
      </RightMessage>
      {submitted && (
        <>
          <LeftMessage initial={visibilityVariants.hidden} animate={visibilityVariants.visible}>
            {isCorrect ? (
              <StyledChatBotHappyIcon data-testid="chatBotHappyIcon" />
            ) : (
              <StyledChatBotSadIcon data-testid="chatBotSadIcon" />
            )}
            <LeftBubble
              initial={bubbleVariants.hidden}
              animate={bubbleVariants.visible}
              transition={{ delay: DELAY_AFTER_SUBMIT }}
              data-testid="resultFeedback"
            >
              {resultsDialog.botResponseTitle}
            </LeftBubble>
          </LeftMessage>
          {!!resultsDialog.answers.length && (
            <RightMessage initial={visibilityVariants.hidden} animate={visibilityVariants.visible}>
              <Bubbles>
                {resultsDialog.answers.map((answer, index) => (
                  <RightBubble
                    as={motion.p}
                    key={index}
                    onClick={answer.onClick}
                    initial={bubbleVariants.hidden}
                    animate={bubbleVariants.visible}
                    transition={{ delay: DELAY_AFTER_SUBMIT + 1 + index * 0.3 }}
                    data-testid="resultAnswer"
                  >
                    {answer?.title}
                  </RightBubble>
                ))}
              </Bubbles>
              <ProfileAvatar />
            </RightMessage>
          )}
        </>
      )}
      <Element name={BOTTOM_ELEMENT_NAME} />
    </>
  );
};

export default ChatQuestion;

const LeftMessage = styled(motion.li)`
  display: flex;
  align-items: flex-end;
  max-width: 85%;
  gap: ${TINY_MARGIN_PX};

  &:not(:last-of-type) {
    margin-bottom: ${SUBMIDDLE_MARGIN_PX};
  }
`;
const RightMessage = styled(LeftMessage)`
  justify-content: flex-end;
  margin-left: auto;
  text-align: right;
`;
const createStyledChatBotIcon = (tag) => styled(tag)`
  width: 36px;
  min-width: 36px;
  transition: 0.3s;
`;
const StyledChatBotHappyIcon = createStyledChatBotIcon(ChatBotHappyIcon);
const StyledChatBotSadIcon = createStyledChatBotIcon(ChatBotSadIcon);
const StyledChatBotIcon = createStyledChatBotIcon(ChatBotIcon);
const Bubbles = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${TINY_MARGIN_PX};
`;
